/**
 * List-view config for the Audiences page: the search/sort wiring the shared list-page pattern
 * consumes (`useListControls` + `filterSortItems` + `ListToolbar`), plus the small display helpers the
 * card and table renderers share. The row type itself is `AudienceListItem` (`../types`). There is no
 * status filter: the page lists saved audiences only (ACT2-583), so a Saved/Draft facet would have
 * nothing to facet.
 */
import type { SortOption } from '@/_shared/components/list/ListToolbar';
import type { ListDeriveConfig, SortState } from '@/_shared/lib/list-controls';
import { warehouseTypeMeta } from '@/settings/components/warehouseAccount/warehouseTypes';

import type { AudienceListItem } from '../types';

export type AudienceSortKey = 'updated' | 'title';

export const AUDIENCE_SORT_KEYS: readonly AudienceSortKey[] = ['updated', 'title'];

export const AUDIENCE_DEFAULT_SORT: SortState<AudienceSortKey> = { key: 'updated', dir: 'desc' };

export const AUDIENCE_SORT_OPTIONS: readonly SortOption[] = [
  { value: 'updated', label: 'Recently updated' },
  { value: 'title', label: 'Name' },
];

/**
 * "1 activation" / "3 activations" for the Audiences list card chip (ACT2-640). Counts are local
 * `audience_activations` rows from `audience.list` — not the live RudderStack sync count the detail
 * header's AudienceActivationsLink uses. Callers that hide at zero should not call this with 0.
 */
export function activationCountLabel(count: number): string {
  return `${count} ${count === 1 ? 'activation' : 'activations'}`;
}

/**
 * Hover text and the warehouse half of {@link audienceRowLabel}. Uses the account name when we
 * have one. Uses the type label when the warehouse list is still loading or the warehouse is gone.
 * Never a raw uuid — that is not a name.
 */
export function audienceWarehouseDisplayName(label: string, type: string): string {
  return label || warehouseTypeMeta(type).label;
}

/**
 * Accessible name for the table row button and the card link (the card prefixes `Open `).
 * An `aria-label` replaces descendant text, so the warehouse must live in this string.
 */
export function audienceRowLabel(item: Pick<AudienceListItem, 'title' | 'warehouseLabel' | 'warehouseType'>): string {
  return `${item.title}, ${audienceWarehouseDisplayName(item.warehouseLabel, item.warehouseType)}`;
}

/** `stale` only means something once a count has actually run — an un-run audience isn't "stale". */
export function isCountStale(item: AudienceListItem): boolean {
  return item.stale && item.countState.status !== 'not-run';
}

const byString = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0);

/** Search covers the title + warehouse; sort by updated / name. No filter facets. */
export const AUDIENCE_DERIVE_CONFIG: ListDeriveConfig<AudienceListItem, AudienceSortKey, never> = {
  searchText: (a) => [a.title, a.warehouseLabel],
  comparators: {
    title: (a, b) => a.title.localeCompare(b.title),
    updated: (a, b) => byString(a.updatedAt, b.updatedAt),
  },
  filterPredicates: {},
};
